import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from '../../lib/axios'
import {
  ArrowLeft,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  HelpCircle,
  Clock,
  Mail,
  ShieldCheck,
  AlertCircle
} from 'lucide-react'
import '../Pages.css'

export default function PlacementTestResults() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [test, setTest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const pollRef = useRef(null)

  const fetchTest = () => {
    axios
      .get(`api/placement/tests/${id}`)
      .then((res) => {
        setTest(res.data)
        setError(null)
        // Stop polling if completed or failed
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(pollRef.current)
          pollRef.current = null
        }
      })
      .catch((err) => {
        console.error(err)
        setError('Failed to load test results')
        clearInterval(pollRef.current)
        pollRef.current = null
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchTest()
    // Start polling
    pollRef.current = setInterval(fetchTest, 5000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [id])

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this placement test?')) return
    setDeleting(true)
    try {
      await axios.delete(`api/placement/tests/${id}`)
      navigate('/inbox-placement')
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete test')
      setDeleting(false)
    }
  }

  const getInboxRateColor = (rate) => {
    if (rate >= 80) return '#22c55e'
    if (rate >= 50) return '#eab308'
    return '#ef4444'
  }

  const getPlacementIcon = (placement) => {
    switch (placement) {
      case 'inbox':
        return <CheckCircle size={18} style={{ color: '#22c55e' }} />
      case 'spam':
        return <XCircle size={18} style={{ color: '#ef4444' }} />
      default:
        return <HelpCircle size={18} style={{ color: 'var(--color-text-muted)' }} />
    }
  }

  const getPlacementLabel = (placement) => {
    switch (placement) {
      case 'inbox': return 'Inbox'
      case 'spam': return 'Spam'
      default: return 'Not Found'
    }
  }

  const getPlacementBadgeStyle = (placement) => {
    switch (placement) {
      case 'inbox':
        return { background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }
      case 'spam':
        return { background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }
      default:
        return { background: 'rgba(148, 163, 184, 0.1)', color: 'var(--color-text-muted)' }
    }
  }

  if (loading) {
    return (
      <div className="page animate-fade-in">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div className="loader"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="page animate-fade-in">
        <div className="page-header">
          <div className="header-info">
            <h2>Test Results</h2>
          </div>
          <button onClick={() => navigate('/inbox-placement')} className="btn btn-ghost">
            <ArrowLeft size={18} />
            <span>Back to Tests</span>
          </button>
        </div>
        <div className="card" style={{
          padding: '1.5rem',
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.05) 100%)',
          border: '1px solid rgba(239, 68, 68, 0.2)'
        }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <AlertCircle size={20} style={{ color: '#ef4444' }} />
            <span style={{ color: '#dc2626', flex: 1 }}>{error}</span>
            <button onClick={fetchTest} className="btn btn-primary" style={{ padding: '0.5rem 1rem' }}>
              <RefreshCw size={16} />
              <span>Retry</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  const isPending = test && ['pending', 'sent', 'checking'].includes(test.status)
  const isCompleted = test?.status === 'completed'
  const isFailed = test?.status === 'failed'

  // Compute summary from results
  const results = test?.results || []
  const inboxCount = results.filter((r) => r.placement === 'inbox').length
  const spamCount = results.filter((r) => r.placement === 'spam').length
  const notFoundCount = results.filter((r) => r.placement !== 'inbox' && r.placement !== 'spam').length
  const totalSeeds = results.length || test?.total_seeds || 5
  const inboxRate = test?.inbox_rate != null ? test.inbox_rate : (totalSeeds > 0 ? (inboxCount / totalSeeds) * 100 : 0)

  return (
    <div className="page animate-fade-in">
      <div className="page-header">
        <div className="header-info">
          <h2>{test?.subject || 'Placement Test Results'}</h2>
          <p>
            {isCompleted && 'Test completed — see results below'}
            {isPending && 'Test in progress — checking inboxes...'}
            {isFailed && 'Test failed — please try again'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={() => navigate('/inbox-placement')} className="btn btn-ghost">
            <ArrowLeft size={18} />
            <span>Back</span>
          </button>
          <button
            onClick={handleDelete}
            className="btn btn-ghost"
            disabled={deleting}
            style={{ color: '#ef4444' }}
          >
            {deleting ? <RefreshCw size={16} className="animate-spin" /> : <Trash2 size={16} />}
            <span>Delete</span>
          </button>
        </div>
      </div>

      {/* Pending/Checking State */}
      {isPending && (
        <div className="card" style={{ maxWidth: '600px', margin: '2rem auto' }}>
          <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
            <div style={{
              width: '80px', height: '80px', margin: '0 auto 1.5rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: '50%', animation: 'pulse 2s infinite'
            }}>
              <Mail size={40} style={{ color: 'white' }} />
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem' }}>
              {test.status === 'pending' && 'Preparing to send...'}
              {test.status === 'sent' && 'Emails sent, waiting for delivery...'}
              {test.status === 'checking' && 'Checking seed inboxes...'}
            </h3>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
              This usually takes 1-3 minutes. The page will update automatically.
            </p>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.5rem 1rem', background: 'var(--color-bg-primary)',
              borderRadius: 'var(--radius-md)', fontSize: '0.85rem', color: 'var(--color-text-muted)'
            }}>
              <RefreshCw size={14} className="animate-spin" />
              Polling every 5 seconds
            </div>
          </div>
        </div>
      )}

      {/* Failed State */}
      {isFailed && (
        <div className="card" style={{ maxWidth: '600px', margin: '2rem auto' }}>
          <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
            <div style={{
              width: '80px', height: '80px', margin: '0 auto 1.5rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              borderRadius: '50%'
            }}>
              <XCircle size={40} style={{ color: 'white' }} />
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: '#ef4444' }}>
              Test Failed
            </h3>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
              {test.error || 'Something went wrong while running this test. Please try creating a new test.'}
            </p>
          </div>
        </div>
      )}

      {/* Completed Results */}
      {isCompleted && (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
            {/* Inbox Rate */}
            <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
              <div style={{
                width: '80px', height: '80px', margin: '0 auto 1rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '50%',
                background: `conic-gradient(${getInboxRateColor(inboxRate)} ${inboxRate * 3.6}deg, var(--color-bg-tertiary) 0deg)`
              }}>
                <div style={{
                  width: '64px', height: '64px', borderRadius: '50%',
                  background: 'var(--color-bg-card)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.25rem', fontWeight: '700', color: getInboxRateColor(inboxRate)
                }}>
                  {Math.round(inboxRate)}%
                </div>
              </div>
              <div style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--color-text-secondary)' }}>
                Inbox Rate
              </div>
            </div>

            {/* Inbox/Spam/Not Found counts */}
            <div className="card" style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <CheckCircle size={18} style={{ color: '#22c55e' }} />
                    <span style={{ fontSize: '0.9rem' }}>Inbox</span>
                  </div>
                  <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#22c55e' }}>{inboxCount}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <XCircle size={18} style={{ color: '#ef4444' }} />
                    <span style={{ fontSize: '0.9rem' }}>Spam</span>
                  </div>
                  <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ef4444' }}>{spamCount}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <HelpCircle size={18} style={{ color: 'var(--color-text-muted)' }} />
                    <span style={{ fontSize: '0.9rem' }}>Not Delivered</span>
                  </div>
                  <span style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--color-text-muted)' }}>{notFoundCount}</span>
                </div>
              </div>
            </div>

            {/* Test Info */}
            <div className="card" style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.25rem' }}>Subject</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>{test.subject || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.25rem' }}>Tested On</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                    {test.created_at ? new Date(test.created_at).toLocaleString() : '—'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.25rem' }}>Total Seeds</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>{totalSeeds}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Detail Table */}
          {results.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3>
                  <ShieldCheck size={18} />
                  Seed Account Details
                </h3>
              </div>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Seed Account</th>
                      <th>Placement</th>
                      <th>Delivery Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result, idx) => {
                      const badgeStyle = getPlacementBadgeStyle(result.placement)
                      return (
                        <tr key={idx}>
                          <td>
                            <div className="email-cell">
                              <div className="email-avatar">
                                {(result.email || result.seed_email || '?').charAt(0).toUpperCase()}
                              </div>
                              <span>{result.email || result.seed_email || `Seed ${idx + 1}`}</span>
                            </div>
                          </td>
                          <td>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                              padding: '0.375rem 0.75rem', borderRadius: 'var(--radius-full)',
                              fontSize: '0.8rem', fontWeight: '500',
                              ...badgeStyle
                            }}>
                              {getPlacementIcon(result.placement)}
                              {getPlacementLabel(result.placement)}
                            </span>
                          </td>
                          <td style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                            {result.delivery_time || result.delivered_at || '—'}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
